// ============================================
// Lab Upload Module Types
// Calibration Pipeline + Post-Launch Architecture
// ============================================

import type { DecisionPoint } from './clinicalCalibration';
import type { FileRejectionCode } from '../services/fileUploadErrors';

// --- File Ingestion Types (CAP.01 D#6) ---

export type UploadMode = 'file' | 'camera';

export type UploadProcessingStatus = 'pending' | 'processing' | 'completed' | 'failed';

export type SecurityFlagType =
  | 'spoofed_extension'
  | 'virus_detected'
  | 'rate_limited';

export interface CameraImage {
  pageNumber: number;
  imageId: string;
}

export interface ValidationResult {
  sizeCheck: boolean;
  typeCheck: boolean;
  contentTypeMatch: boolean;
  virusScanClean: boolean;
  structuralIntegrity: boolean;
}

/**
 * LabUpload — Created per upload with full validation metadata.
 * Maps 1:1 to the LabUpload interface in hlx-cap-01-file-ingestion.md.
 */
export interface LabUpload {
  fileId: string;
  patientSessionId: string;
  uploadMode: UploadMode;
  originalFilename: string;
  pageCount: number;
  images?: CameraImage[];
  fileSize: number;
  mimeType: string;
  uploadTimestamp: number;
  processingStatus: UploadProcessingStatus;
  validationResult: ValidationResult;
  rejectionReason?: string;
  securityFlags: SecurityFlagType[];
}

/**
 * Upload event — logged for every validation outcome (acceptance or rejection).
 * Collection: upload_events
 */
export type UploadEventType = 'accepted' | 'rejected';

export type CorruptionType =
  | 'pdf_too_small'
  | 'missing_pdf_header'
  | 'missing_eof_marker'
  | 'png_too_small'
  | 'invalid_png_signature'
  | 'png_missing_ihdr'
  | 'jpeg_too_small'
  | 'missing_jpeg_soi'
  | 'missing_jpeg_eoi'
  | 'unsupported_mime';

export interface UploadEvent {
  eventId: string;
  fileId: string;
  patientSessionId: string;
  eventType: UploadEventType;
  timestamp: number;
  rejectionCode?: FileRejectionCode;
  rejectionReason?: string;
  corruptionType?: CorruptionType;
  securityFlags: SecurityFlagType[];
  validationResult: ValidationResult;
  uploadMode: UploadMode;
  fileSize: number;
  mimeType: string;
}

// --- Extraction Types (Section 5) ---

export type GovernanceStatus = 'approved' | 'pending_review';

export type QuarantineReason =
  | 'unidentified_biomarker'
  | 'unit_mismatch'
  | 'no_approved_bounds'
  | 'multiple';

export interface ExtractedBiomarker {
  rawName: string;
  canonicalId: string | null;
  value: number | null;
  unit: string;
  confidence: number;
  rawText: string;
  pageNumber: number;
  parseWarnings: string[];
  standardRange?: { low: number; high: number };
  governanceStatus?: GovernanceStatus;
  quarantineReason?: QuarantineReason;
  quarantineDetails?: string;
}

// --- AliasedBiomarker (CAP.02 D#10 — Post-Resolution Output) ---
// Stage 2 of the schema chain. Produced by CAP.02 alias resolution,
// consumed by CAP.08 for clinical range application.

export type BiomarkerFlag = 'normal' | 'high' | 'low' | 'critical';

export interface AliasedBiomarkerSourceLocation {
  page: number;
  x: number;
  y: number;
}

export interface AliasedBiomarker {
  /** Canonical biomarker ID from alias table, or "unidentified" */
  canonicalName: string;
  /** Raw text from document (= RawExtractedBiomarker.name) */
  originalName: string;
  /** Extracted value — numeric or string for qualitative results */
  value: number | string;
  /** Normalized unit (if conversion available) */
  unit: string;
  /** Original unit before normalization */
  originalUnit: string;
  /** Whether unit was normalized to standard. Downstream consumers MUST check before clinical calculations. */
  isNormalized: boolean;
  /** Reference range as reported on lab document */
  referenceRange: string;
  /** Lab-reported flag */
  flag: BiomarkerFlag | null;
  /** OCR confidence (0-1) or 1.0 for text PDFs */
  confidence: number;
  /** Location in source document */
  sourceLocation: AliasedBiomarkerSourceLocation;
  /** Processing metadata flags */
  processingFlags: string[];
  /** Why this canonical name was chosen — populated for context_resolved and fuzzy_matched entries */
  resolutionNotes?: string;
}

// --- Clinical Taxonomy (Section 15.8.2) ---

export type RootCause =
  | 'adrenal'
  | 'thyroid'
  | 'hormonal'
  | 'metabolic'
  | 'inflammatory'
  | 'nutritional'
  | 'hepatic'
  | 'lifestyle'
  | 'multi_system'
  | 'unclassified';

export interface PrimaryFinding {
  rootCause: RootCause;
  confidence: number;
  supportingMarkers: string[];
  clinicalReasoning: string;
}

// --- Lab Session (Section 7) ---

export type LabSessionStatus =
  | 'uploaded'
  | 'extracting'
  | 'extracted'
  | 'analyzing'
  | 'complete'
  | 'failed';

export interface LabSession {
  sessionId: string;
  brandId: string;
  patientId?: string;
  uploadedAt: string;
  status: LabSessionStatus;
  source: 'pdf' | 'photo' | 'manual';
  fileName: string;
  fileSize: number;
  extractedBiomarkers: ExtractedBiomarker[];
  phiGateResult?: PHIGateResult;
  retestOf: string | null;
  errorMessage?: string;
  metadata: Record<string, unknown>;
}

// --- PHI Gate (Section 9) ---

export interface PHIGateResult {
  passed: boolean;
  findingsCount: number;
  redactedFields: string[];
  checkedAt: string;
}

// --- Calibration Case (Section 15.7) ---

export type CalibrationCaseStatus =
  | 'imported'
  | 'phi_checked'
  | 'extracted'
  | 'blind_run_complete'
  | 'compared'
  | 'reviewed'
  | 'complete'
  | 'failed';

export interface CaseDemographics {
  age?: number;
  sex?: 'Male' | 'Female';
}

export interface BlindRunOutput {
  diagnosis: string;
  protocol: string;
  recommendedProducts: string[];
  rootCauses: PrimaryFinding[];
  followUpTimeline?: string;
  contraindications?: string[];
  rawResponse: string;
  promptVersionId: string;
  modelUsed: string;
  completedAt: string;
}

export interface DrCAssessment {
  diagnosis: string;
  products: string[];
  labOrders: string[];
  notes: string;
  extractedFrom: 'soap_pdf' | 'manual_entry';
}

export interface DrCReview {
  rating: 'yes' | 'mostly' | 'no';
  comment?: string;
  reviewedAt: string;
}

export interface CalibrationCase {
  caseId: string;
  brandId: string;
  physicianId: string;
  labSessionId: string;
  soapFileBase64?: string;
  soapFileName?: string;
  demographics?: CaseDemographics;
  status: CalibrationCaseStatus;
  blindRunOutput?: BlindRunOutput;
  drCAssessment?: DrCAssessment;
  decisionPoints?: DecisionPoint[];
  alignmentScore?: number;
  drCReview?: DrCReview;
  primaryFindings?: PrimaryFinding[];
  extractedBiomarkers?: ExtractedBiomarker[];
  importedAt: string;
  completedAt?: string;
  errorMessage?: string;
}

// --- Clinical Analysis Prompt (Section 15.6) ---

export interface ClinicalAnalysisPrompt {
  versionId: string;
  systemPrompt: string;
  userPromptTemplate: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  changeReason?: string;
  parentVersionId?: string;
}

// --- Generic-to-Formulary Mapping (Section 15.8.3) ---

export interface GenericFormularyMapping {
  mappingId: string;
  genericName: string;
  formularyProductId: string;
  confidence: 'exact' | 'close' | 'approximate';
  notes?: string;
}

// --- Convergence Tracking (Section 15.8.1) ---

export interface ConvergenceRecord {
  recordId: string;
  caseId: string;
  modelCorrect: boolean;
  drCorrect: boolean;
  bothCorrect: boolean;
  divergenceAreas: string[];
  alignmentScore: number;
  recordedAt: string;
}

// --- Launch Gate (PI-007) ---

export interface LaunchGateThresholds {
  minCases: number;
  minWomenCases: number;
  minOverallAlignment: number;
  minDP5: number;
  minDP7: number;
  maxUnresolved: number;
}

export const DEFAULT_LAUNCH_GATE_THRESHOLDS: LaunchGateThresholds = {
  minCases: 50,
  minWomenCases: 20,
  minOverallAlignment: 75,
  minDP5: 85,
  minDP7: 85,
  maxUnresolved: 0,
};

export interface LaunchGateStatus {
  totalCasesReviewed: number;
  womenCases35to60: number;
  overallAlignmentScore: number;
  dp5Score: number;
  dp7Score: number;
  unresolvedHighStake: number;
  thresholds: LaunchGateThresholds;
  passing: boolean;
  computedAt: string;
}

// --- Divergence Summary (Section 15.8.4) ---

export interface DivergencePattern {
  decisionArea: string;
  frequency: number;
  description: string;
}

export interface DivergenceSummary {
  summaryId: string;
  batchRange: { start: number; end: number };
  patterns: DivergencePattern[];
  proposedPromptChange: string;
  approvedByStan: boolean;
  rejectionReason?: string;
  appliedAt?: string;
  createdAt: string;
}

// --- Post-Launch Stubs (Section 15.11) ---

export interface PrescriptionGroundTruth {
  prescriptionId: string;
  openLoopOrderId: string;
  prescriberId: string;
  providerAssessment: string;
  productsOrdered: string[];
  receivedAt: string;
}

export interface LiveCasePromotion {
  patientId: string;
  labSessionId: string;
  promotedAt: string;
  promotedBy: string;
  reason: string;
}

export interface OutcomeMetric {
  patientId: string;
  baselineLabSessionId: string;
  retestLabSessionId: string;
  biomarkerId: string;
  baselineValue: number;
  retestValue: number;
  percentChange: number;
  timespanDays: number;
}
